import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { EnterpriseFooter } from "@/pages/enterprise/Enterprise";
import {
  Lock, ShieldCheck, Database, Server, Users, FileText, ArrowRight,
  AlertTriangle, Clock, CheckCircle2, Eye, Key,
} from "lucide-react";

export default function Security() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 border-b border-border bg-card/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-14 flex items-center justify-between">
          <Link to="/" className="font-display font-black text-lg tracking-tight">
            Rule<span className="text-primary">AI</span><span className="text-muted-foreground">.systems</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <Link to="/enterprise" className="hover:text-primary">Enterprise</Link>
            <Link to="/security" className="text-primary font-medium">Security</Link>
            <Link to="/library" className="hover:text-primary">Library</Link>
            <Link to="/about" className="hover:text-primary">Company</Link>
            <Link to="/contact"><Button size="sm" className="rounded-sm bg-primary hover:bg-primary/90">Contact</Button></Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
          <div className="overline text-primary mb-4">/ TRUST CENTER</div>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tight leading-tight" data-testid="security-hero">
            Security &amp;<br/>Compliance.
          </h1>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-3xl">
            RuleAI.Systems handles the operational data that manufacturers, quality departments, and industrial operators use to run their business. Below is a plain-language summary of how we protect it, what standards we align to today, and what we&rsquo;re pursuing next.
          </p>
          <div className="mt-6 text-xs font-mono-plex uppercase tracking-widest text-muted-foreground">
            Last updated: February 2026 &middot; Version 1.0
          </div>
        </div>
      </section>

      {/* Live commitments */}
      <section className="border-b border-border bg-zinc-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
          <div className="overline text-emerald-600 mb-4">◆ LIVE COMMITMENTS</div>
          <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-8">
            What we do today, in production.
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <SecCard icon={Lock} title="Encryption in transit"
              body="All traffic to RuleAI.Systems is served over TLS 1.2 or newer. Modern cipher suites only. HTTP is rejected at the edge." />
            <SecCard icon={Database} title="Encryption at rest"
              body="Production data lives in MongoDB Atlas with AES-256 encryption at rest on managed storage volumes." />
            <SecCard icon={Key} title="Password hashing"
              body="User passwords are hashed with bcrypt (cost factor ≥ 12). Plaintext passwords are never logged or stored." />
            <SecCard icon={ShieldCheck} title="Token-based authentication"
              body="Session tokens are signed JWTs with an expiration window. All authenticated API calls verify signature and expiration." />
            <SecCard icon={Users} title="Role-based access control"
              body="Multi-tenant workspace isolation. Owner / Admin / Editor / Viewer roles enforced at every API endpoint. No data crosses org boundaries." />
            <SecCard icon={FileText} title="Immutable audit log"
              body="Every SOP action, record change, approval, and comment is logged with actor identity, timestamp, and before/after state." />
            <SecCard icon={Server} title="Redundant hosting"
              body="Application infrastructure runs across multiple availability zones. Automatic failover and load balancing at the ingress layer." />
            <SecCard icon={Clock} title="Backup &amp; recovery"
              body="Automated daily snapshots of production data. Point-in-time recovery available. Recovery point objective ≤ 24 hours." />
            <SecCard icon={Eye} title="Environment isolation"
              body="Production, preview, and development environments are fully isolated. No production data ever crosses into non-production." />
            <SecCard icon={AlertTriangle} title="Vulnerability response"
              body="We accept responsible disclosure at security@ruleai.systems. Critical vulnerabilities are triaged within 24 hours; remediated within 7 days." />
          </div>
        </div>
      </section>

      {/* Data handling */}
      <section className="border-b border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
          <div className="overline text-primary mb-4">/ DATA HANDLING</div>
          <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-6">
            Your data, plainly explained.
          </h2>
          <div className="space-y-6 text-sm leading-relaxed">
            <DataItem q="Where is my data stored?"
              a="All production data is stored in the United States on managed cloud infrastructure (MongoDB Atlas). Enterprise customers can request a dedicated instance region." />
            <DataItem q="Does RuleAI train AI models on my data?"
              a="No. Content you or your team enter into RuleAI is never used to train third-party or in-house AI models. When you invoke an AI feature, only the specific request payload is sent to the model provider (Anthropic Claude Sonnet 4.6), and no training data agreement is granted." />
            <DataItem q="Which third-party sub-processors do you use?"
              a="MongoDB Atlas (data storage, US), Anthropic Claude (AI features), Stripe (payments, when enabled). We do not resell your data to any third party." />
            <DataItem q="Can I export my data?"
              a="Yes. Every SOP, record, comment, and spreadsheet you create can be exported to CSV, PDF, or DOCX at any time. Enterprise customers get bulk export tools." />
            <DataItem q="What happens if I close my account?"
              a="Your active data is retained for 30 days after account closure to allow recovery, then permanently deleted from our production systems. Backups are purged within 90 days." />
            <DataItem q="How do you handle personal data (GDPR / CCPA)?"
              a="We collect only what&rsquo;s necessary to operate the service: email, name, organization, and product usage. Users can request deletion or export by emailing support@ruleai.systems." />
          </div>
        </div>
      </section>

      {/* Compliance roadmap */}
      <section className="border-b border-border bg-zinc-50">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16">
          <div className="overline text-primary mb-4">/ COMPLIANCE ROADMAP</div>
          <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-2">
            What we&rsquo;re pursuing next.
          </h2>
          <p className="text-muted-foreground text-sm max-w-2xl mb-8">
            We publish our compliance posture honestly. Below is where we are today, and where we&rsquo;re headed. We do not claim certifications we do not hold.
          </p>
          <div className="border border-border bg-card rounded-sm divide-y divide-border">
            {[
              { name: "GDPR / CCPA data handling", status: "Aligned", desc: "Data subject rights supported by export + deletion via support ticket." },
              { name: "ISO 27001 alignment", status: "In progress", desc: "Internal controls audited quarterly. Formal certification targeted 2026 Q4." },
              { name: "SOC 2 Type I", status: "Targeted 2026 Q4", desc: "Engagement to begin with a licensed auditor after we cross 100 paying customers." },
              { name: "SOC 2 Type II", status: "Targeted 2027", desc: "Follows successful Type I engagement, 6-month observation window." },
              { name: "ISO 9001 (internal QMS)", status: "Live via RuleAI", desc: "We eat our own dog food &mdash; RuleAI is our own QMS." },
              { name: "HIPAA (for healthcare enterprise)", status: "On request", desc: "BAAs available for enterprise healthcare customers. Contact sales." },
            ].map((row) => (
              <div key={row.name} className="px-6 py-4 grid grid-cols-12 gap-4 items-center">
                <div className="col-span-12 md:col-span-5 font-medium text-sm">{row.name}</div>
                <div className="col-span-6 md:col-span-3">
                  <span className={`text-[10px] font-mono-plex uppercase tracking-widest px-2 py-1 rounded-sm ${
                    row.status === "Aligned" || row.status === "Live via RuleAI"
                      ? "bg-emerald-100 text-emerald-800"
                      : row.status === "In progress"
                      ? "bg-blue-100 text-blue-800"
                      : row.status === "On request"
                      ? "bg-secondary text-foreground"
                      : "bg-amber-100 text-amber-800"
                  }`}>{row.status}</span>
                </div>
                <div className="col-span-6 md:col-span-4 text-xs text-muted-foreground">{row.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section>
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16 text-center">
          <h2 className="font-display font-bold text-3xl tracking-tight">Security questions?</h2>
          <p className="mt-4 text-muted-foreground max-w-xl mx-auto">
            Enterprise buyers can request our security questionnaire responses, our SIG Lite, and a call with the founders.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <a href="mailto:security@ruleai.systems">
              <Button className="rounded-sm bg-primary hover:bg-primary/90 h-11 px-6" data-testid="security-email-btn">
                Email security@ruleai.systems <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </a>
            <Link to="/enterprise">
              <Button variant="outline" className="rounded-sm h-11 px-6">
                Enterprise overview
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <EnterpriseFooter />
    </div>
  );
}

function SecCard({ icon: Icon, title, body }) {
  return (
    <div className="border border-border bg-card p-5 rounded-sm">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-sm bg-primary/10 grid place-items-center shrink-0">
          <Icon className="w-4 h-4 text-primary" />
        </div>
        <div>
          <div className="font-display font-bold text-base">{title}</div>
          <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{body}</p>
        </div>
      </div>
    </div>
  );
}

function DataItem({ q, a }) {
  return (
    <div className="border-l-2 border-primary pl-5 py-1">
      <div className="font-display font-bold text-base">{q}</div>
      <p className="text-muted-foreground mt-2">{a}</p>
    </div>
  );
}
